import ls from 'localstorage-slim';
import React from 'react';
import {Tag} from 'antd';
import NameWithLink from '../../components/NameWithLink';
import StatusMark from '../../components/StatusMark';
import StatusLine from '../../components/pages/SubscriptionsPage/StatusLine';
import VendorTypeIcon from '../../components/pages/VendorsPage/VendorTypeIcon';
import VendorStatusSelector from '../../components/pages/VendorsPage/VendorStatusSelector';
import {OptionsList} from './form.helpers';
import routes from '../../routes/routes.json';
import {vendorsPageConstants} from '../../pages/VendorsPage/vendorsPage.constants';
import {beneficiaryValidationResultStatuses, vendorPriorityConstants, vendorStatusConstants} from '../../constants';

const storedCardTableColumns = 'stored-vendors-table-columns-v2';

const vendorPriorityKeys = {
  [vendorPriorityConstants.OCCASIONAL]: 'vendorPriorities.occasional',
  [vendorPriorityConstants.STRATEGIC]: 'vendorPriorities.strategic',
  [vendorPriorityConstants.TACTICAL]: 'vendorPriorities.tactical',
};

const vendorStatuses = {
  [vendorStatusConstants.EMPTY]: {
    key: 'vendorStatuses.empty',
    color: '#B5B8BF'
  },
  [vendorStatusConstants.ACTIVE]: {
    key: 'vendorStatuses.active',
    color: '#52C41A'
  },
  [vendorStatusConstants.INACTIVE]: {
    key: 'vendorStatuses.inactive',
    color: '#00465D'
  },
  [vendorStatusConstants.TO_BE_VALIDATED]: {
    key: 'vendorStatuses.missingVAT',
    color: '#722ED1'
  },
};

export const VendorPriorityOptions = (activeValue) => OptionsList(vendorPriorityKeys, activeValue);

export const VendorStatusesOptions = (showOnlyVerified = false) => {
  const statuses = Object.keys(vendorStatuses).reduce((acc, value) => {
    acc[value] = vendorStatuses[value]?.key;
    return acc;
  }, {});

  const allowedStatuses = showOnlyVerified
    ? [vendorStatusConstants.ACTIVE, vendorStatusConstants.INACTIVE]
    : Object.values(vendorStatusConstants).filter(status => status !== vendorStatusConstants.EMPTY);

  return OptionsList(statuses).map(option => {
    let color = vendorStatuses[option.value]?.color;
    return {
      ...option,
      image: <StatusLine color={color} />
    }
  }).filter(status => allowedStatuses.includes(status.value));
}

export const vendorsHelpers = {
  getStoredVendorsTableColumns: () => ls.get(storedCardTableColumns),
  getVendorNameWithLink: (vendor, variant = 'system') => {
    if (!vendor) return null;
    const name = vendor?.name;
    const url = vendorsHelpers.getVendorDetailsUrl(vendor);
    return <NameWithLink name={name} url={url} variant={variant} />;
  },
  getVendorPriority: (value) => vendorPriorityKeys[value] || undefined,
  getVendorStatusMarkOrSelector: ({onChange, vendor} = {}) => {
    if (vendor?.status === undefined) return ;
    const isAvailableSelector = [vendorStatusConstants.ACTIVE, vendorStatusConstants.INACTIVE].includes(vendor.status);
    return isAvailableSelector
      ? (
        <VendorStatusSelector
          vendor={vendor}
          onChange={(value, domEvent) => onChange && onChange({domEvent, value, vendor})}
        />
      )
      : vendorsHelpers.getVendorStatusMark(vendor);
  },
  getVendorStatusMark: (vendor) => (
    <StatusMark
      statusCode={vendor.status}
      statuses={vendorStatuses}
    />
  ),
  getVendorTags: (tags = []) => tags.map((tag, index) => <Tag key={index}>{tag}</Tag>),
  getVendorTypeIcon: (vendor) => <VendorTypeIcon vendor={vendor} />,
  storeVendorsTableColumns: (value) => ls.set(storedCardTableColumns, value),
  getDefaultVendorsTableColumns: (defaultKeys) => vendorsHelpers.getStoredVendorsTableColumns() || defaultKeys,
  getVendorDetailsUrl: (vendor) => {
    const id = vendor?.vendor_id;
    const name = vendor?.name;
    if (!id) return null;

    return `${routes.vendorsList}?${vendorsPageConstants.SELECTED_VENDOR_ID}=${id}&search=${name}`;
  },
  isVendorToBeValidated: (vendor) => Boolean(vendor && vendor.status === vendorStatusConstants.TO_BE_VALIDATED),
  isVendorAccountingReference: (vendor) => Boolean(vendor && vendor.accounting_external_reference),
  getBeneficiaryValidationDetails: ({validationDetails, t} = {}) => {
    const beneficiaryValidationId = validationDetails?.beneficiary_validation_id;
    if (!validationDetails || !validationDetails.vop) {
      return {isValid: false, beneficiaryValidationId};
    }

    const {matched_name, result} = validationDetails.vop;

    let alertType = 'warning';
    let message = '';

    switch (result) {
      case beneficiaryValidationResultStatuses.MATCH:
        message = t(`vendors:beneficiaryValidationResults.${beneficiaryValidationResultStatuses.MATCH}`);
        alertType = 'success';
        break;
      case beneficiaryValidationResultStatuses.CLOSE_MATCH:
        message = t(`vendors:beneficiaryValidationResults.${beneficiaryValidationResultStatuses.CLOSE_MATCH}`, {matched_name});
        break;
      case beneficiaryValidationResultStatuses.NO_MATCH:
        message = t(`vendors:beneficiaryValidationResults.${beneficiaryValidationResultStatuses.NO_MATCH}`);
        break;
      case beneficiaryValidationResultStatuses.VERIFICATION_POSSIBLE:
        message = t(`vendors:beneficiaryValidationResults.${beneficiaryValidationResultStatuses.VERIFICATION_POSSIBLE}`);
        break;
      default:
        message = t('vendors:beneficiaryValidationResults.unknownValidationResult');
        break;
    }
    return { isValid: true, alertType, message, beneficiaryValidationId };
  }
}
